import React, { useState, useEffect } from 'react';
import { WaitlistService } from '../services/waitlist.service';
import { SearchableCitySelect } from './SearchableCitySelect';

export interface WaitlistInitialData {
  name?: string;
  storeName?: string;
  email?: string;
}

interface WaitlistModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialTab?: 'customer' | 'vendor' | 'rider';
  initialData?: WaitlistInitialData;
}

const CATEGORY_OPTIONS = [
  'Fresh Produce (Vegetables & Fruits)',
  'Grains & Cereals',
  'Poultry & Meat',
  'Tubers & Yams',
  'Seafood & Fish',
  'Spices & Seasonings',
  'Dairy & Eggs',
  'Oils & Processed Foods',
];

export const WaitlistModal: React.FC<WaitlistModalProps> = ({
  isOpen,
  onClose,
  initialTab = 'customer',
  initialData,
}) => {
  const [activeTab, setActiveTab] = useState<'customer' | 'vendor' | 'rider'>(initialTab);

  // Customer Form State
  const [customerForm, setCustomerForm] = useState({
    name: '',
    email: '',
    phone: '',
    city: '',
  });

  // Vendor Form State
  const [vendorForm, setVendorForm] = useState({
    storeName: '',
    email: '',
    phone: '',
    city: '',
    productCategories: [] as string[],
  });

  // Rider Form State
  const [riderForm, setRiderForm] = useState({
    name: '',
    email: '',
    phone: '',
    city: '',
  });

  // Status & Validation State
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [customerPhoneWarning, setCustomerPhoneWarning] = useState(false);
  const [vendorPhoneWarning, setVendorPhoneWarning] = useState(false);
  const [riderPhoneWarning, setRiderPhoneWarning] = useState(false);

  useEffect(() => {
    setActiveTab(initialTab);
  }, [initialTab]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      setErrorMsg('');
      setSuccessMsg('');
      setSubmitted(false);
      setCustomerPhoneWarning(false);
      setVendorPhoneWarning(false);
      setRiderPhoneWarning(false);

      if (initialData) {
        if (initialData.email) {
          setCustomerForm((prev) => ({ ...prev, email: initialData.email || '' }));
          setVendorForm((prev) => ({ ...prev, email: initialData.email || '' }));
          setRiderForm((prev) => ({ ...prev, email: initialData.email || '' }));
        }
        if (initialData.name) {
          setCustomerForm((prev) => ({ ...prev, name: initialData.name || '' }));
          setRiderForm((prev) => ({ ...prev, name: initialData.name || '' }));
        }
        if (initialData.storeName) {
          setVendorForm((prev) => ({ ...prev, storeName: initialData.storeName || '' }));
        }
      }
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen, initialData]);

  if (!isOpen) return null;

  const validateEmail = (email: string) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const handlePhoneChange = (val: string, formType: 'customer' | 'vendor' | 'rider') => {
    let digits = val.replace(/\D/g, '');
    let hadLeadingZero = false;

    if (digits.startsWith('0')) {
      hadLeadingZero = true;
      digits = digits.replace(/^0+/, '');
    }

    digits = digits.slice(0, 10);

    if (formType === 'customer') {
      setCustomerPhoneWarning(hadLeadingZero);
      setCustomerForm((prev) => ({ ...prev, phone: digits }));
    } else if (formType === 'vendor') {
      setVendorPhoneWarning(hadLeadingZero);
      setVendorForm((prev) => ({ ...prev, phone: digits }));
    } else {
      setRiderPhoneWarning(hadLeadingZero);
      setRiderForm((prev) => ({ ...prev, phone: digits }));
    }
  };

  const formatPhoneForBackend = (rawPhone: string) => {
    const trimmed = rawPhone.trim();
    if (!trimmed) return undefined;
    return trimmed.startsWith('0') ? trimmed : `0${trimmed}`;
  };

  const handleCustomerSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMsg('');

    if (!customerForm.email.trim()) {
      setErrorMsg('Please enter your email address.');
      return;
    }

    if (!validateEmail(customerForm.email.trim())) {
      setErrorMsg('Please enter a valid email address.');
      return;
    }

    setLoading(true);
    const result = await WaitlistService.submitCustomerWaitlist({
      email: customerForm.email.trim(),
      name: customerForm.name.trim() || undefined,
      phone: formatPhoneForBackend(customerForm.phone),
      city: customerForm.city.trim() || undefined,
    });
    setLoading(false);

    if (result.success) {
      setSuccessMsg(result.message);
      setSubmitted(true);
    } else {
      setErrorMsg(result.message);
    }
  };

  const handleVendorSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMsg('');

    if (!vendorForm.storeName.trim()) {
      setErrorMsg('Please enter your store or farm name.');
      return;
    }

    if (!vendorForm.email.trim()) {
      setErrorMsg('Please enter your contact email address.');
      return;
    }

    if (!validateEmail(vendorForm.email.trim())) {
      setErrorMsg('Please enter a valid email address.');
      return;
    }

    setLoading(true);
    const result = await WaitlistService.submitVendorWaitlist({
      storeName: vendorForm.storeName.trim(),
      email: vendorForm.email.trim(),
      phone: formatPhoneForBackend(vendorForm.phone),
      city: vendorForm.city.trim() || undefined,
      productCategories:
        vendorForm.productCategories.length > 0 ? vendorForm.productCategories : undefined,
    });
    setLoading(false);

    if (result.success) {
      setSuccessMsg(result.message);
      setSubmitted(true);
    } else {
      setErrorMsg(result.message);
    }
  };

  const handleRiderSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMsg('');

    if (!riderForm.name.trim()) {
      setErrorMsg('Please enter your name or dispatch service name.');
      return;
    }

    if (!riderForm.email.trim()) {
      setErrorMsg('Please enter your contact email address.');
      return;
    }

    if (!validateEmail(riderForm.email.trim())) {
      setErrorMsg('Please enter a valid email address.');
      return;
    }

    setLoading(true);
    const result = await WaitlistService.submitRiderWaitlist({
      name: riderForm.name.trim(),
      email: riderForm.email.trim(),
      phone: formatPhoneForBackend(riderForm.phone),
      city: riderForm.city.trim() || undefined,
    });
    setLoading(false);

    if (result.success) {
      setSuccessMsg(result.message);
      setSubmitted(true);
    } else {
      setErrorMsg(result.message);
    }
  };

  const toggleCategory = (cat: string) => {
    setVendorForm((prev) => {
      const exists = prev.productCategories.includes(cat);
      return {
        ...prev,
        productCategories: exists
          ? prev.productCategories.filter((c) => c !== cat)
          : [...prev.productCategories, cat],
      };
    });
  };

  const resetForm = () => {
    setSubmitted(false);
    setErrorMsg('');
    setSuccessMsg('');
    setCustomerPhoneWarning(false);
    setVendorPhoneWarning(false);
    setRiderPhoneWarning(false);
    setCustomerForm({ name: '', email: '', phone: '', city: '' });
    setVendorForm({ storeName: '', email: '', phone: '', city: '', productCategories: [] });
    setRiderForm({ name: '', email: '', phone: '', city: '' });
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 overflow-y-auto"
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.7)', backdropFilter: 'blur(5px)' }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="relative w-full max-w-lg rounded-3xl p-6 sm:p-8 shadow-2xl transition-all my-8"
        style={{ backgroundColor: '#18181b', color: '#ffffff' }}
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-5 right-5 w-9 h-9 rounded-full border border-slate-700 bg-slate-800 text-slate-300 hover:text-white hover:bg-slate-700 flex items-center justify-center font-bold text-base transition-all"
          aria-label="Close"
        >
          ✕
        </button>

        {/* Tab Switcher */}
        <div className="flex items-center p-1 rounded-2xl bg-slate-900 border border-slate-800 mb-6 gap-1">
          <button
            type="button"
            onClick={() => {
              setActiveTab('customer');
              resetForm();
            }}
            className={`flex-1 py-2.5 px-3 rounded-xl text-xs font-bold transition-all ${
              activeTab === 'customer'
                ? 'bg-emerald-600 text-white shadow-md'
                : 'text-slate-400 hover:text-white'
            }`}
          >
            Customer
          </button>
          <button
            type="button"
            onClick={() => {
              setActiveTab('vendor');
              resetForm();
            }}
            className={`flex-1 py-2.5 px-3 rounded-xl text-xs font-bold transition-all ${
              activeTab === 'vendor'
                ? 'bg-orange-500 text-white shadow-md'
                : 'text-slate-400 hover:text-white'
            }`}
          >
            Farmer / Merchant
          </button>
          <button
            type="button"
            onClick={() => {
              setActiveTab('rider');
              resetForm();
            }}
            className={`flex-1 py-2.5 px-3 rounded-xl text-xs font-bold transition-all ${
              activeTab === 'rider'
                ? 'bg-sky-600 text-white shadow-md'
                : 'text-slate-400 hover:text-white'
            }`}
          >
            Delivery Rider
          </button>
        </div>

        {/* Form Content */}
        {submitted ? (
          /* Success Screen */
          <div className="text-center py-6 space-y-4 animate-in fade-in zoom-in-95">
            <div className="w-16 h-16 rounded-full bg-emerald-500/20 text-emerald-400 border-2 border-emerald-500 flex items-center justify-center mx-auto text-3xl">
              ✓
            </div>
            <h3 className="text-2xl font-black text-white">You're On The List!</h3>
            <p className="text-sm text-slate-300 leading-relaxed max-w-sm mx-auto">
              {successMsg ||
                "Thank you for joining MarketMate! We'll notify you via email as soon as early access opens in your city."}
            </p>
            <div className="pt-4 flex flex-col gap-3">
              <button
                type="button"
                onClick={() => {
                  onClose();
                  window.location.href = '/launch';
                }}
                className="w-full px-6 py-3 rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white font-bold text-xs shadow-lg shadow-emerald-950/50 flex items-center justify-center gap-2"
              >
                <span>View Live Launch Countdown 🚀</span>
              </button>
              <div className="flex flex-col sm:flex-row gap-2.5 justify-center">
                <button
                  type="button"
                  onClick={resetForm}
                  className="flex-1 px-4 py-2.5 rounded-xl border border-slate-700 text-slate-300 hover:text-white hover:bg-slate-800 font-bold text-xs"
                >
                  Submit Another Entry
                </button>
                <button
                  type="button"
                  onClick={onClose}
                  className="flex-1 px-4 py-2.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-200 font-bold text-xs"
                >
                  Done &amp; Return
                </button>
              </div>
            </div>
          </div>
        ) : activeTab === 'customer' ? (
          /* Customer Waitlist Form */
          <form onSubmit={handleCustomerSubmit} className="space-y-4">
            <div className="text-center mb-5">
              <h2 className="text-2xl font-black text-white mb-1">Join MarketMate Waitlist</h2>
              <p className="text-xs text-slate-400">
                Get early access delivery discounts when fresh farm foodstuff arrives in your market
              </p>
            </div>

            {errorMsg && (
              <div className="p-3 rounded-xl bg-rose-500/10 border border-rose-500/30 text-rose-400 text-xs font-semibold text-center">
                {errorMsg}
              </div>
            )}

            <div>
              <label className="block text-xs font-bold text-slate-300 uppercase tracking-wider mb-1">
                Email Address <span className="text-emerald-400">*</span>
              </label>
              <input
                type="email"
                required
                value={customerForm.email}
                onChange={(e) => setCustomerForm({ ...customerForm, email: e.target.value })}
                placeholder="name@example.com"
                className="w-full px-4 py-3 rounded-xl bg-slate-900 border border-slate-700 text-white placeholder-slate-500 text-sm font-medium focus:border-emerald-500 focus:outline-none"
              />
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-300 uppercase tracking-wider mb-1">
                Full Name (Optional)
              </label>
              <input
                type="text"
                value={customerForm.name}
                onChange={(e) => setCustomerForm({ ...customerForm, name: e.target.value })}
                placeholder="e.g. Amina Bello"
                className="w-full px-4 py-3 rounded-xl bg-slate-900 border border-slate-700 text-white placeholder-slate-500 text-sm font-medium focus:border-emerald-500 focus:outline-none"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-bold text-slate-300 uppercase tracking-wider mb-1">
                  Phone Number (Optional)
                </label>
                <div className="flex rounded-xl overflow-hidden border border-slate-700 bg-slate-900 focus-within:border-emerald-500">
                  <span className="px-3 py-3 text-xs font-bold text-emerald-400 bg-slate-800 flex items-center justify-center border-r border-slate-700">
                    +234
                  </span>
                  <input
                    type="tel"
                    maxLength={10}
                    value={customerForm.phone}
                    onChange={(e) => handlePhoneChange(e.target.value, 'customer')}
                    placeholder="9012345678"
                    className="w-full px-3 py-3 bg-transparent text-white placeholder-slate-500 text-sm font-medium focus:outline-none"
                  />
                </div>
                {customerPhoneWarning ? (
                  <p className="text-[11px] text-amber-400 font-semibold mt-1">
                    Note: Do not start with 0. Enter 10 digits (e.g. 9012345678).
                  </p>
                ) : (
                  <p className="text-[11px] text-slate-400 mt-1">
                    Do not start with 0. Enter 10 digits (e.g. 9012345678).
                  </p>
                )}
              </div>

              <SearchableCitySelect
                value={customerForm.city}
                onChange={(cityVal) => setCustomerForm({ ...customerForm, city: cityVal })}
                accentColor="emerald"
                label="City / Location"
                placeholder="Search Abeokuta, Ikeja, Lekki..."
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full mt-4 py-3.5 px-6 rounded-2xl bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-700 text-white font-bold text-sm shadow-lg flex items-center justify-center gap-2 transition-all"
            >
              {loading ? (
                <span>Submitting to Waitlist...</span>
              ) : (
                <>
                  <span>Join Customer Waitlist</span>
                  <span className="text-base">→</span>
                </>
              )}
            </button>
          </form>
        ) : activeTab === 'vendor' ? (
          /* Vendor / Merchant Waitlist Form */
          <form onSubmit={handleVendorSubmit} className="space-y-4">
            <div className="text-center mb-5">
              <h2 className="text-2xl font-black text-white mb-1">Become a Merchant Partner</h2>
              <p className="text-xs text-slate-400">
                Register your farm or grocery store for zero onboarding fees and reduced commission rates
              </p>
            </div>

            {errorMsg && (
              <div className="p-3 rounded-xl bg-rose-500/10 border border-rose-500/30 text-rose-400 text-xs font-semibold text-center">
                {errorMsg}
              </div>
            )}

            <div>
              <label className="block text-xs font-bold text-slate-300 uppercase tracking-wider mb-1">
                Store or Farm Name <span className="text-orange-400">*</span>
              </label>
              <input
                type="text"
                required
                value={vendorForm.storeName}
                onChange={(e) => setVendorForm({ ...vendorForm, storeName: e.target.value })}
                placeholder="e.g. Mile 12 Organic Farm Store"
                className="w-full px-4 py-3 rounded-xl bg-slate-900 border border-slate-700 text-white placeholder-slate-500 text-sm font-medium focus:border-orange-500 focus:outline-none"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-bold text-slate-300 uppercase tracking-wider mb-1">
                  Contact Email <span className="text-orange-400">*</span>
                </label>
                <input
                  type="email"
                  required
                  value={vendorForm.email}
                  onChange={(e) => setVendorForm({ ...vendorForm, email: e.target.value })}
                  placeholder="vendor@example.com"
                  className="w-full px-4 py-3 rounded-xl bg-slate-900 border border-slate-700 text-white placeholder-slate-500 text-sm font-medium focus:border-orange-500 focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-300 uppercase tracking-wider mb-1">
                  Phone Number
                </label>
                <div className="flex rounded-xl overflow-hidden border border-slate-700 bg-slate-900 focus-within:border-orange-500">
                  <span className="px-3 py-3 text-xs font-bold text-orange-400 bg-slate-800 flex items-center justify-center border-r border-slate-700">
                    +234
                  </span>
                  <input
                    type="tel"
                    maxLength={10}
                    value={vendorForm.phone}
                    onChange={(e) => handlePhoneChange(e.target.value, 'vendor')}
                    placeholder="9012345678"
                    className="w-full px-3 py-3 bg-transparent text-white placeholder-slate-500 text-sm font-medium focus:outline-none"
                  />
                </div>
                {vendorPhoneWarning ? (
                  <p className="text-[11px] text-amber-400 font-semibold mt-1">
                    Note: Do not start with 0. Enter 10 digits (e.g. 9012345678).
                  </p>
                ) : (
                  <p className="text-[11px] text-slate-400 mt-1">
                    Do not start with 0. Enter 10 digits (e.g. 9012345678).
                  </p>
                )}
              </div>
            </div>

            <SearchableCitySelect
              value={vendorForm.city}
              onChange={(cityVal) => setVendorForm({ ...vendorForm, city: cityVal })}
              accentColor="orange"
              label="Main Market Location / City"
              placeholder="Search Abeokuta, Ota, Sango, Ikeja..."
            />

            {/* Product Category Selection */}
            <div>
              <label className="block text-xs font-bold text-slate-300 uppercase tracking-wider mb-2">
                Produce Categories (Select All That Apply)
              </label>
              <div className="flex flex-wrap gap-1.5 max-h-32 overflow-y-auto p-2 bg-slate-900 rounded-xl border border-slate-800">
                {CATEGORY_OPTIONS.map((cat) => {
                  const selected = vendorForm.productCategories.includes(cat);
                  return (
                    <button
                      key={cat}
                      type="button"
                      onClick={() => toggleCategory(cat)}
                      className={`px-2.5 py-1 rounded-lg text-[11px] font-bold transition-all ${
                        selected
                          ? 'bg-orange-500 text-white'
                          : 'bg-slate-800 text-slate-400 hover:bg-slate-700 hover:text-white'
                      }`}
                    >
                      {selected ? '✓ ' : '+ '}
                      {cat}
                    </button>
                  );
                })}
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full mt-4 py-3.5 px-6 rounded-2xl bg-orange-500 hover:bg-orange-600 disabled:bg-slate-700 text-white font-bold text-sm shadow-lg flex items-center justify-center gap-2 transition-all"
            >
              {loading ? (
                <span>Submitting Merchant Application...</span>
              ) : (
                <>
                  <span>Apply for VIP Merchant Access</span>
                  <span className="text-base">→</span>
                </>
              )}
            </button>
          </form>
        ) : (
          /* Delivery Rider Waitlist Form */
          <form onSubmit={handleRiderSubmit} className="space-y-4">
            <div className="text-center mb-5">
              <h2 className="text-2xl font-black text-white mb-1">Become a Delivery Partner</h2>
              <p className="text-xs text-slate-400">
                Register your vehicle or dispatch service for priority order dispatch and rider signup bonuses
              </p>
            </div>

            {errorMsg && (
              <div className="p-3 rounded-xl bg-rose-500/10 border border-rose-500/30 text-rose-400 text-xs font-semibold text-center">
                {errorMsg}
              </div>
            )}

            <div>
              <label className="block text-xs font-bold text-slate-300 uppercase tracking-wider mb-1">
                Full Name / Dispatch Service <span className="text-sky-400">*</span>
              </label>
              <input
                type="text"
                required
                value={riderForm.name}
                onChange={(e) => setRiderForm({ ...riderForm, name: e.target.value })}
                placeholder="e.g. Babatunde Express / Bike Rider"
                className="w-full px-4 py-3 rounded-xl bg-slate-900 border border-slate-700 text-white placeholder-slate-500 text-sm font-medium focus:border-sky-500 focus:outline-none"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-bold text-slate-300 uppercase tracking-wider mb-1">
                  Contact Email <span className="text-sky-400">*</span>
                </label>
                <input
                  type="email"
                  required
                  value={riderForm.email}
                  onChange={(e) => setRiderForm({ ...riderForm, email: e.target.value })}
                  placeholder="rider@example.com"
                  className="w-full px-4 py-3 rounded-xl bg-slate-900 border border-slate-700 text-white placeholder-slate-500 text-sm font-medium focus:border-sky-500 focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-300 uppercase tracking-wider mb-1">
                  Phone Number
                </label>
                <div className="flex rounded-xl overflow-hidden border border-slate-700 bg-slate-900 focus-within:border-sky-500">
                  <span className="px-3 py-3 text-xs font-bold text-sky-400 bg-slate-800 flex items-center justify-center border-r border-slate-700">
                    +234
                  </span>
                  <input
                    type="tel"
                    maxLength={10}
                    value={riderForm.phone}
                    onChange={(e) => handlePhoneChange(e.target.value, 'rider')}
                    placeholder="9012345678"
                    className="w-full px-3 py-3 bg-transparent text-white placeholder-slate-500 text-sm font-medium focus:outline-none"
                  />
                </div>
                {riderPhoneWarning ? (
                  <p className="text-[11px] text-amber-400 font-semibold mt-1">
                    Note: Do not start with 0. Enter 10 digits (e.g. 9012345678).
                  </p>
                ) : (
                  <p className="text-[11px] text-slate-400 mt-1">
                    Do not start with 0. Enter 10 digits (e.g. 9012345678).
                  </p>
                )}
              </div>
            </div>

            <SearchableCitySelect
              value={riderForm.city}
              onChange={(cityVal) => setRiderForm({ ...riderForm, city: cityVal })}
              accentColor="sky"
              label="Operating City / Location"
              placeholder="Search Abeokuta, Ikeja, Lekki, Sango..."
            />

            <button
              type="submit"
              disabled={loading}
              className="w-full mt-4 py-3.5 px-6 rounded-2xl bg-sky-600 hover:bg-sky-700 disabled:bg-slate-700 text-white font-bold text-sm shadow-lg flex items-center justify-center gap-2 transition-all"
            >
              {loading ? (
                <span>Submitting Rider Application...</span>
              ) : (
                <>
                  <span>Join Rider Waitlist</span>
                  <span className="text-base">→</span>
                </>
              )}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};
